
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Star, User, Calendar, Trash2 } from "lucide-react"
import { useReview, useUpdateReviewNotes, useArchiveReview } from "@/api/reviews"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface ReviewDetailDialogProps {
  reviewId: string | null
  open: boolean
  onClose: () => void
}

export function ReviewDetailDialog({
  reviewId,
  open,
  onClose,
}: ReviewDetailDialogProps) {
  const { t } = useTranslation()
  const [adminNotes, setAdminNotes] = useState('')
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)

  const { data: review, isLoading } = useReview(reviewId || '')
  const updateNotesMutation = useUpdateReviewNotes()
  const archiveReviewMutation = useArchiveReview()

  useEffect(() => {
    if (review) {
      setAdminNotes(review.adminNotes || '')
    }
  }, [review])

  const handleSaveNotes = async () => {
    if (!reviewId) return
    
    await updateNotesMutation.mutateAsync({
      reviewId,
      adminNotes,
    })
  }

  const handleArchive = async () => {
    if (!reviewId) return
    
    await archiveReviewMutation.mutateAsync(reviewId)
    setShowArchiveDialog(false)
    onClose()
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-6 w-6 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-xl font-semibold">{rating}/5</span>
      </div>
    )
  }

  const getClassName = () => {
    if (!review?.classId) return null
    return review.classId.label || review.classId.name
  }

  const getInstructorName = () => {
    if (!review?.staffId) return null
    return `${review.staffId.name} ${review.staffId.surname}`
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('reviews.reviewDetails')}</DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-4 py-8">
              <div className="h-8 bg-gray-200 animate-pulse rounded w-1/2" />
              <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
              <div className="h-20 bg-gray-200 animate-pulse rounded" />
            </div>
          ) : review ? (
            <div className="space-y-6">
              {/* Rating Display */}
              <div>
                <Label className="text-sm text-muted-foreground">
                  {t('reviews.rating')}
                </Label>
                <div className="mt-2">{renderStars(review.rating)}</div>
              </div>

              {/* Member Info */}
              <div>
                <Label className="text-sm text-muted-foreground">
                  {t('reviews.submittedBy')}
                </Label>
                <div className="mt-2 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{review.userName}</div>
                    {review.userId?.email && (
                      <div className="text-sm text-muted-foreground">
                        {review.userId.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Class & Instructor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    {t('reviews.class')}
                  </Label>
                  <div className="mt-2 font-medium">
                    {getClassName() || '-'}
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    {t('reviews.instructor')}
                  </Label>
                  <div className="mt-2 font-medium">
                    {getInstructorName() || '-'}
                  </div>
                </div>
              </div>

              {/* Feedback */}
              {review.feedback && (
                <div>
                  <Label className="text-sm text-muted-foreground">
                    {t('reviews.feedback')}
                  </Label>
                  <div className="mt-2 bg-muted rounded-lg p-4">
                    <p className="text-sm whitespace-pre-wrap">
                      {review.feedback}
                    </p>
                  </div>
                </div>
              )}

              {/* Date */}
              <div>
                <Label className="text-sm text-muted-foreground">
                  {t('reviews.submittedOn')}
                </Label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {new Date(review.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <Label htmlFor="adminNotes">
                  {t('reviews.adminNotes')}
                </Label>
                <Textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={t('reviews.adminNotesPlaceholder')}
                  className="mt-2 h-32"
                />
                <Button
                  onClick={handleSaveNotes}
                  disabled={updateNotesMutation.isPending}
                  className="mt-2"
                >
                  {updateNotesMutation.isPending
                    ? t('common.saving')
                    : t('reviews.saveNotes')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              {t('reviews.noReviews')}
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <Button
              variant="destructive"
              onClick={() => setShowArchiveDialog(true)}
              disabled={archiveReviewMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {t('reviews.archiveReview')}
            </Button>
            <Button variant="outline" onClick={onClose}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reviews.archiveReview')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('reviews.confirmArchive')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
